import { atom, useAtom } from "jotai";
import { supabase } from "../lib/supabase";
import { useEffect, useState } from "react";
import { useUserPosition } from "../hooks/useUserPosition";
import { useAuth } from "../hooks/useAuth";

export type AvailableStore = {
  store_id: number;
  store_name: string;
  distance_meters: number;
};

export interface AvailableStoresOptions {
  userId?: string;
  position?: { latitude: number; longitude: number };
}

const storesAtom = atom<AvailableStore[]>([]);
const loadedAtom = atom<boolean>(false);

export function useResetAvailableStores() {
  const [, setStores] = useAtom(storesAtom);
  const [, setLoaded] = useAtom(loadedAtom);
  return () => {
    setStores([]);
    setLoaded(false);
  };
}

export function useAvailableStores(options?: AvailableStoresOptions) {
  const [stores, setStores] = useAtom(storesAtom);
  const [loaded, setLoaded] = useAtom(loadedAtom);
  const { position: browserPosition } = useUserPosition();
  const { user } = useAuth();

  const isOverridden = !!(options?.userId && options?.position);
  const effectivePosition = isOverridden ? options.position : browserPosition;
  const effectiveUserId = isOverridden ? options.userId : user?.id;

  // When overridden (view-as mode), use local state instead of global atoms
  // to avoid polluting the rep's store list cache
  const [overriddenStores, setOverriddenStores] = useState<AvailableStore[]>(
    [],
  );
  const [overriddenLoading, setOverriddenLoading] = useState(false);

  const [loading, setLoading] = useState(!loaded);

  // Standard fetch (rep mode) — uses global atoms
  useEffect(() => {
    if (isOverridden) return;
    if (!effectivePosition || !effectiveUserId) return;

    setLoading(true);
    const fn = async () => {
      const { data, error } = await supabase.rpc(
        "stores_available_for_proofing",
        {
          p_longitude: effectivePosition.longitude,
          p_latitude: effectivePosition.latitude,
          p_user_id: effectiveUserId,
        },
      );

      if (error) throw error;
      return data || [];
    };

    fn().then((data) => {
      setStores(data as AvailableStore[]);
      setLoaded(true);
      setLoading(false);
    });
  }, [isOverridden, setStores, setLoaded, effectivePosition, effectiveUserId]);

  // Override fetch (view-as mode) — uses local state
  useEffect(() => {
    if (!isOverridden) return;
    if (!effectivePosition || !effectiveUserId) return;

    setOverriddenLoading(true);
    const fn = async () => {
      const { data, error } = await supabase.rpc(
        "stores_available_for_proofing",
        {
          p_longitude: effectivePosition.longitude,
          p_latitude: effectivePosition.latitude,
          p_user_id: effectiveUserId,
        },
      );

      if (error) throw error;
      return data || [];
    };

    fn().then((data) => {
      setOverriddenStores(data as AvailableStore[]);
      setOverriddenLoading(false);
    });
  }, [isOverridden, effectivePosition, effectiveUserId]);

  if (isOverridden) {
    return { stores: overriddenStores, loading: overriddenLoading };
  }

  return { stores, loading };
}
